/**
 * Tiny helpers shared across flows. Kept dependency-free so the
 * auth-page bundle stays small (no React / no UI framework).
 *
 * IMPORTANT: every helper here builds DOM via `createElement` rather
 * than `innerHTML`. Query-string params (`?email=…`, `?phone=…`) are
 * partner-supplied and could carry HTML — using textContent / value
 * keeps them inert.
 */

import { User } from 'firebase/auth';
import { reply, PostMessageContext } from './postmessage';
import { HorusAuthMessageBody } from './protocol';
import {
  readBrandingFromUrl,
  brandHeader,
  brandFooter,
  BrandingConfig,
} from './branding';

const root = document.getElementById('root') as HTMLElement;

// Branding cache — main.ts calls setBranding() once on boot with the
// policy-enforced version (so flows render with the white-label tier
// honored, not the raw URL params). Default to URL-read raw so
// flows that mount before main.ts finishes its async work still get
// reasonable chrome.
let branding: BrandingConfig = readBrandingFromUrl();

/** Called by main.ts after the policy fetch resolves. */
export function setBranding(b: BrandingConfig): void {
  branding = b;
}

/**
 * Replace the root content. Wraps the flow's inner node with the
 * partner's brand header (logo + name) + footer (terms / privacy /
 * powered-by). Brand chrome only renders when branding is configured;
 * unbranded popups look identical to before.
 */
export function mount(node: Node): HTMLElement {
  // `#root` ships hidden so flows that redirect away (Google) never
  // flash a card. Any flow that actually renders reveals it here, and
  // drops the white pre-render background so the themed one applies.
  root.hidden = false;
  document.body.classList.remove('booting');
  while (root.firstChild) root.removeChild(root.firstChild);
  const header = brandHeader(branding);
  if (header) root.appendChild(header);
  root.appendChild(node);
  root.appendChild(brandFooter(branding));
  return root;
}

/** Tiny element factory — `el('input', { type: 'email', placeholder: 'email' })` */
export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Partial<Record<string, string | boolean | number>> = {},
  children: (Node | string)[] = []
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) {
    if (v === false || v === undefined) continue;
    if (k === 'class') node.className = String(v);
    else if (k.startsWith('data-')) node.setAttribute(k, String(v));
    else if (k in node) (node as unknown as Record<string, unknown>)[k] = v;
    else node.setAttribute(k, String(v));
  }
  for (const c of children) {
    node.appendChild(typeof c === 'string' ? document.createTextNode(c) : c);
  }
  return node;
}

export function showError(message: string): void {
  const elBox = document.querySelector<HTMLElement>('[data-error]');
  if (elBox) {
    elBox.textContent = message;
    elBox.style.display = 'block';
  }
}

export function clearError(): void {
  const elBox = document.querySelector<HTMLElement>('[data-error]');
  if (elBox) {
    elBox.textContent = '';
    elBox.style.display = 'none';
  }
}

export function setBusy(busy: boolean): void {
  document.querySelectorAll<HTMLButtonElement>('button').forEach((b) => {
    b.disabled = busy;
  });
}

/**
 * Standard "we got a Firebase user, post the token + close" flow.
 * Every successful sign-in funnels through this so the success
 * message shape stays consistent.
 *
 * This function used to read only `user.getIdToken()`, which made every
 * session unrecoverable after an hour: the refresh token lived on the
 * `User` right here, was never read, and died with `window.close()` 200ms
 * later. The SDK then stored `refreshToken: ''`, so its own refresh timer
 * POSTed an empty string, got a 400 back, and swallowed it silently.
 *
 * The refresh token now crosses the boundary, but only to an origin the app
 * has actually registered. See the two-tier note in postmessage.ts.
 */
export async function postSuccess(
  ctx: PostMessageContext,
  user: User,
  providerId?: string
): Promise<void> {
  const idToken = await user.getIdToken();
  const decoded = decodeJwtPayload(idToken);
  const expiresAt = decoded?.exp;
  const body: HorusAuthMessageBody = {
    type: 'success',
    idToken,
    // Populated by the Firebase SDK on every success path. Omitted entirely
    // for an unregistered origin — that partner keeps the one-hour session
    // it has always had, rather than gaining a credential that never dies.
    refreshToken: ctx.refreshAllowed ? user.refreshToken || undefined : undefined,
    expiresAt: typeof expiresAt === 'number' ? expiresAt : undefined,
    user: {
      uid: user.uid,
      email: user.email ?? undefined,
      phone: user.phoneNumber ?? undefined,
      displayName: user.displayName ?? undefined,
      photoURL: user.photoURL ?? undefined,
      providerId: providerId ?? user.providerData[0]?.providerId,
    },
  };
  reply(ctx, body);
  // Give the opener a tick to receive the message before we close.
  setTimeout(() => window.close(), 200);
}

export function postError(ctx: PostMessageContext, code: string, message: string): void {
  reply(ctx, { type: 'error', code, message });
}

export function postCancel(ctx: PostMessageContext): void {
  reply(ctx, { type: 'cancel' });
  setTimeout(() => window.close(), 100);
}

export function postReady(ctx: PostMessageContext): void {
  reply(ctx, { type: 'ready' });
}

/**
 * Signal that the reveal flow is finished. Carries NO key material —
 * the private key never crosses the postMessage boundary, only a
 * "did the user actually view the key" boolean for audit / UI hints.
 */
export function postRevealComplete(
  ctx: PostMessageContext,
  viewed: boolean
): void {
  reply(ctx, { type: 'reveal_complete', viewed });
}

/**
 * Decode JWT payload without verifying — we only use it to read `exp`
 * for the SDK's convenience. The token itself was minted by Firebase
 * server-side and is verified by the Horus backend on the next call.
 */
function decodeJwtPayload(jwt: string): Record<string, unknown> | null {
  try {
    const [, payload] = jwt.split('.');
    if (!payload) return null;
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(json);
  } catch {
    return null;
  }
}

export function getQueryParam(name: string): string | undefined {
  return new URLSearchParams(window.location.search).get(name) ?? undefined;
}

/**
 * Maps Firebase auth/* error codes to user-readable strings. The
 * `code` half is preserved on the wire so the SDK can branch on
 * specific failures programmatically.
 */
export function humanizeFirebaseError(err: unknown): { code: string; message: string } {
  const code = (err as { code?: string })?.code ?? 'auth/unknown';
  switch (code) {
    case 'auth/invalid-email':
      return { code, message: 'That email address looks malformed.' };
    case 'auth/wrong-password':
    case 'auth/invalid-credential':
      return { code, message: 'Email or password is incorrect.' };
    case 'auth/user-not-found':
      return { code, message: 'No account found with that email.' };
    case 'auth/email-already-in-use':
      return { code, message: 'An account with that email already exists. Try signing in.' };
    case 'auth/weak-password':
      return { code, message: 'Password must be at least 6 characters.' };
    case 'auth/too-many-requests':
      return { code, message: 'Too many attempts. Wait a few minutes before trying again.' };
    case 'auth/popup-blocked':
      return { code, message: 'Popup blocked by browser. Allow popups for auth.horuswallet.com.' };
    case 'auth/popup-closed-by-user':
      return { code, message: 'Sign-in was cancelled.' };
    case 'auth/invalid-phone-number':
      return { code, message: 'Phone number must be in E.164 format (e.g. +15551234567).' };
    case 'auth/invalid-verification-code':
      return { code, message: 'Verification code is incorrect.' };
    default:
      return { code, message: (err as Error)?.message ?? 'Sign-in failed.' };
  }
}
